import { schema } from 'normalizr';
import { flattenFields } from '../../shared/utils/normalizeData';

const idAttribute = ({ sys: { id } }) => id;
const processStrategy = ({ fields, sys }) => ({
  id: sys.id,
  ...flattenFields(fields),
  sys,
});

export const linkSchema = new schema.Entity(
  'links',
  {},
  {
    idAttribute,
  }
);

export const regionSchema = new schema.Entity(
  'regions',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const assetSchema = new schema.Entity(
  'assets',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const categorySchema = new schema.Entity(
  'categories',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const experienceSchema = new schema.Entity(
  'experiences',
  {
    market: linkSchema,
    vendor: linkSchema,
    category: [linkSchema],
    heroVideo: linkSchema,
    heroVideoPlaceholder: linkSchema,
    heroGallery: [linkSchema],
    testimonials: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const configSchema = new schema.Entity(
  'appConfig',
  {
    shareImage: assetSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const partnerSchema = new schema.Entity(
  'partners',
  {
    icon: assetSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const webConfigSchema = new schema.Entity(
  'websiteConfig',
  {
    homeVideo: assetSchema,
    homeVideoPoster: assetSchema,
    shareImage: assetSchema,
    featuredMarkets: [linkSchema],
    featuredExperiences: [linkSchema],
    partners: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const footerSchema = new schema.Entity(
  'footer',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const collectionSchema = new schema.Entity(
  'collections',
  {
    experiences: [linkSchema],
    heroMultimedia: linkSchema,
    marker: linkSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const inspireSchema = new schema.Entity(
  'inspire',
  {
    content: [linkSchema],
    experiences: [linkSchema],
    heroMultimedia: linkSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const inspireGallerySchema = new schema.Entity(
  'inspireGallery',
  {
    images: [assetSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const inspireHeadingSchema = new schema.Entity(
  'inspireHeading',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const inspireQuoteSchema = new schema.Entity(
  'inspireQuote',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const inspireTextBlockSchema = new schema.Entity(
  'inspireTextBlock',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const vendorSchema = new schema.Entity(
  'vendors',
  {
    image: linkSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const testimonialSchema = new schema.Entity(
  'testimonials',
  {
    thumbnail: linkSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const marketSchema = new schema.Entity(
  'markets',
  {
    heroMedia: linkSchema,
    videoPoster: linkSchema,
    regions: [linkSchema],
    categories: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const kioskModeSchema = new schema.Entity(
  'kioskModes',
  {
    heroMedia: linkSchema,
    videoPoster: linkSchema,
    market: linkSchema,
    experiences: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const featuredExperiencesSectionSchema = new schema.Entity(
  'featuredExperiencesSections',
  {
    market: linkSchema,
    experiences: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const quoteSchema = new schema.Entity(
  'quotes',
  {
    image: linkSchema,
    article: linkSchema,
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const storySchema = new schema.Entity(
  'stories',
  {
    image: linkSchema,
    author: linkSchema,
    market: linkSchema,
    tags: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const guideSchema = new schema.Entity(
  'guides',
  {
    image: linkSchema,
    author: linkSchema,
    market: linkSchema,
    tags: [linkSchema],
  },
  {
    idAttribute,
    processStrategy,
  }
);

export const newsletterSchema = new schema.Entity(
  'newsletters',
  {},
  {
    idAttribute,
    processStrategy,
  }
);

export const entrySchema = new schema.Union(
  {
    asset: assetSchema,
    experience: experienceSchema,
    appConfig: configSchema,
    websiteConfig: webConfigSchema,
    collection: collectionSchema,
    category: categorySchema,
    inspire: inspireSchema,
    inspireGallery: inspireGallerySchema,
    inspireHeading: inspireHeadingSchema,
    inspireQuote: inspireQuoteSchema,
    inspireTextBlock: inspireTextBlockSchema,
    vendor: vendorSchema,
    testimonial: testimonialSchema,
    market: marketSchema,
    kioskMode: kioskModeSchema,
    featuredExperiencesSection: featuredExperiencesSectionSchema,
    partner: partnerSchema,
    footer: footerSchema,
    region: regionSchema,
    quote: quoteSchema,
    story: storySchema,
    guide: guideSchema,
    newsletter: newsletterSchema,
  },
  value => {
    if (value.sys.type === 'Asset') {
      return 'asset';
    }

    return value.sys.contentType.sys.id;
  }
);

export const entryArraySchema = new schema.Array(entrySchema);
